import type { DirectiveBinding, ObjectDirective } from 'vue'
import useThemeVars from '@lib/composables/useThemeVars'

type TooltipHandlersHtmlElement = HTMLElement & {
  _tooltipHandlers?: { enter: any; click: any }
  _currentTooltip?: HTMLElement | null
  _hideCurrentTooltip?: () => void
  _tooltipValue?: string
}

let disableCustomTooltips = false
const tooltipDirective: ObjectDirective<HTMLElement, string | undefined> = {
  created() {
    disableCustomTooltips = !!window.disable_custom_tooltips
  },
  mounted(el: TooltipHandlersHtmlElement, binding: DirectiveBinding<string | undefined>) {
    el._tooltipValue = binding.value ?? ''
    function createTooltip() {
      const themeVars = useThemeVars()
      const tooltip = document.createElement('div')
      tooltip.className = 'tooltip'
      tooltip.textContent = el._tooltipValue ?? ''
      tooltip.style.cssText = `
        position: fixed;
        background: ${themeVars.value.primary};
        color: ${themeVars.value.base};
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.15s;
        z-index: 9999;
        max-width: 200px;
        word-wrap: break-word;
      `
      ;(document.fullscreenElement || document.body).appendChild(tooltip)
      return tooltip
    }

    function hideCurrentTooltip() {
      if (el._currentTooltip) {
        el._currentTooltip.style.opacity = '0'
        // Remove tooltip after fade out
        setTimeout(() => {
          if (el._currentTooltip) {
            el._currentTooltip.remove()
            el._currentTooltip = null
          }
        }, 150)
        el.removeEventListener('mousemove', updateTooltipPosition)
        el.removeEventListener('mouseleave', hideTooltip)
      }
    }

    function updateTooltipPosition(e: MouseEvent) {
      if (!el._currentTooltip) return

      const offset = 8
      let x = e.clientX + offset
      let y = e.clientY + offset

      // Check if tooltip would go off screen and adjust position
      const rect = el._currentTooltip.getBoundingClientRect()
      if (x + rect.width > window.innerWidth) {
        x = e.clientX - rect.width - offset
      }
      if (y + rect.height > window.innerHeight) {
        y = e.clientY - rect.height - offset
      }

      el._currentTooltip.style.left = `${x}px`
      el._currentTooltip.style.top = `${y}px`
    }

    function hideTooltip() {
      hideCurrentTooltip()
    }

    function showTooltip(e: MouseEvent) {
      if (!el._tooltipValue) return

      // Hide any existing tooltip first
      hideCurrentTooltip()

      // Create tooltip only when needed
      el._currentTooltip = createTooltip()

      el._currentTooltip.style.opacity = '1'
      updateTooltipPosition(e)

      el.addEventListener('mousemove', updateTooltipPosition)
      el.addEventListener('mouseleave', hideTooltip)
    }

    function hideTooltipOnClick() {
      hideCurrentTooltip()
    }

    // NOTE:PLA-31333: Always update aria-label for accessibility/UI testing regardless of tooltip mode
    el.setAttribute('aria-label', binding.value ?? '')
    if (!disableCustomTooltips) {
      // Set up custom tooltip behavior
      el.addEventListener('mouseenter', showTooltip)
      el.addEventListener('click', hideTooltipOnClick)

      // Store handlers and cleanup function for unmount
      el._tooltipHandlers = {
        enter: showTooltip,
        click: hideTooltipOnClick,
      }
      el._hideCurrentTooltip = hideCurrentTooltip
    } else {
      // Simple browser tooltip behavior
      el.setAttribute('title', binding.value ?? '')
    }
  },

  updated(el: TooltipHandlersHtmlElement, binding: DirectiveBinding<string | undefined>) {
    // NOTE: PLA-31333: Always update aria-label for accessibility/UI testing regardless of tooltip mode
    el.setAttribute('aria-label', binding.value ?? '')

    if (disableCustomTooltips) {
      // Update browser tooltip
      el.setAttribute('title', binding.value ?? '')
    } else {
      el._tooltipValue = binding.value ?? ''

      if (el._currentTooltip) {
        el._currentTooltip.textContent = el._tooltipValue
      }
    }
  },

  unmounted(el: TooltipHandlersHtmlElement) {
    // Clean up any currently visible tooltip
    if (el._hideCurrentTooltip) {
      el._hideCurrentTooltip()
    }

    // Remove event listeners
    if (el._tooltipHandlers) {
      el.removeEventListener('mouseenter', el._tooltipHandlers.enter)
      el.removeEventListener('click', el._tooltipHandlers.click)
      delete el._tooltipHandlers
    }

    // Clean up references
    delete el._currentTooltip
    delete el._hideCurrentTooltip
    delete el._tooltipValue

    // Clean up browser tooltip attributes
    el.removeAttribute('title')
    el.removeAttribute('aria-label')
  },
}

export default tooltipDirective
